import {useContext, useEffect, useMemo} from 'react';

import {useTranslation} from 'react-i18next';
import {Navigate, useLocation} from 'react-router-dom';

import {LanguageContext} from '../../../contexts/LanguageContext/LanguageContext';
import QuestionnaireBenefits from '../../../features/company/culture/surveys/components/survey-questionnaire/benefits/QuestionnaireBenefits';
import {QuestionnaireDemographic} from '../../../features/company/culture/surveys/components/survey-questionnaire/demographic/QuestionnaireDemographic';
import OpenFeedback from '../../../features/company/culture/surveys/components/survey-questionnaire/open-feedback/OpenFeedback';
import {QuestionnaireSlides} from '../../../features/company/culture/surveys/components/survey-questionnaire/QuestionnaireSlides/QuestionnaireSlides';
import {ThankYouPage} from '../../../features/company/culture/surveys/components/survey-questionnaire/ThankYouPage/ThankYouPage';
import {TopStrengths} from '../../../features/company/culture/surveys/components/survey-questionnaire/top-strengths/TopStrengths';
import {CompanySurveyConfiguration} from '../../../features/company/culture/surveys/types/SurveyTypes';
import {useCompanyQuestionnaireUpdate} from '../../../hooks/useCompanyQuestionnaireUpdate';
import {
  Answer,
  BenefitsRequestBody,
  Feedback,
  GenerateQuestionnaireRequestBody,
} from '../../../lib/api/RequestBodies/features/company/Questionnaires/QuestionnaireRequestBodies';
import {QuestionnaireSection} from '../../../types/CompanyQuestionnaire';

type QuestionnaireRouteState = GenerateQuestionnaireRequestBody & {
  surveyConfiguration?: CompanySurveyConfiguration;
};

export const CompanyCultureSurveyQuestionnairePage = () => {
  const location = useLocation();
  const routeState = location.state as QuestionnaireRouteState | undefined;
  const {t} = useTranslation();
  const urlParams = new URLSearchParams(window.location.search);
  const currentLanguage = useContext(LanguageContext);
  const surveyCode = urlParams.get('code');
  const questionnairePayload = useMemo<GenerateQuestionnaireRequestBody | undefined>(() => {
    if (!routeState) return undefined;

    return {companySurveyQuestionnaire: routeState.companySurveyQuestionnaire};
  }, [routeState]);
  const omittedSections = useMemo(
    () => (routeState?.surveyConfiguration?.omitFeedback ? [QuestionnaireSection.Feedback] : []),
    [routeState?.surveyConfiguration?.omitFeedback]
  );

  const {questionnaire, submitSection, questions, currentStep, goNext, isQuestionnaireLoading, questionnaireError} =
    useCompanyQuestionnaireUpdate({
      questionnairePayload,
      omittedSections,
    });

  const submitCulture = (answers: Answer[]) => {
    submitSection(QuestionnaireSection.CulturalQuestions, {answers});
  };

  const submitDreamEmployer = (answers: Answer[]) => {
    submitSection(QuestionnaireSection.DreamEmployerAnswers, {answers});
  };

  const submitHappiness = (answers: Answer[]) => {
    submitSection(QuestionnaireSection.HappinessAnswers, {answers});
  };

  const submitBenefits = (answers: BenefitsRequestBody) => {
    submitSection(QuestionnaireSection.Benefits, answers);
  };

  const submitStrengths = (answers: string[]) => {
    submitSection(QuestionnaireSection.Strengths, {answers});
  };

  const submitFeedback = (answers?: Feedback[]) => {
    // Feedback is optional.
    if (!answers) {
      goNext();
      return;
    }
    submitSection(QuestionnaireSection.Feedback, {answers});
  };

  // This displays a warning to the user if they click refresh.
  useEffect(() => {
    // Disables the warning once questionnaire is complete
    if (currentStep === QuestionnaireSection.Complete) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentStep]);

  // Cannot create questionnaire without payload.
  if (!questionnairePayload) {
    const surveyUrl = `/company/culture-survey?code=${surveyCode}&initLang=${currentLanguage}`;
    return <Navigate to={surveyUrl} />;
  }

  if (isQuestionnaireLoading) {
    return null;
  }

  if (questionnairePayload && !questionnaire && questionnaireError) {
    const surveyUrl = `/company/culture-survey?code=${surveyCode}&initLang=${currentLanguage}`;
    return <Navigate to={surveyUrl} replace />;
  }

  switch (currentStep) {
    case QuestionnaireSection.Demographics:
      return (
        <QuestionnaireDemographic onSubmit={submitSection} demographicQuestions={questionnaire?.surveyDemographic} />
      );
    case QuestionnaireSection.CulturalQuestions:
      return (
        <QuestionnaireSlides
          key='step-culture'
          title={t('companySurveys.public.culturalItems.title')}
          items={questions.culture}
          onSubmit={submitCulture}
        />
      );
    case QuestionnaireSection.DreamEmployerAnswers: {
      const title = questionnaire?.dreamEmployerItems?.questions[0].name[currentLanguage] || '';
      const subtitle = {
        top: t('companySurveys.public.dreamEmployer.subtitleOne'),
        bottom: t('companySurveys.public.dreamEmployer.subtitleTwo'),
      };
      return (
        <QuestionnaireSlides
          key='step-dream-employer'
          items={questions.dreamEmployer}
          title={title}
          subtitle={subtitle}
          onSubmit={submitDreamEmployer}
        />
      );
    }

    case QuestionnaireSection.Benefits:
      return (
        <QuestionnaireBenefits
          existingBenefits={questions.existingBenefits}
          desiredBenefits={questions.desiredBenefits}
          onSubmit={submitBenefits}
        />
      );
    case QuestionnaireSection.Strengths:
      return <TopStrengths strengths={questions.strengths} onSubmit={submitStrengths} />;
    case QuestionnaireSection.HappinessAnswers: {
      const title = questionnaire?.happinessItems?.questions[0].name[currentLanguage] || '';

      return (
        <QuestionnaireSlides
          key='step-happiness'
          items={questions.happiness}
          title={title}
          onSubmit={submitHappiness}
        />
      );
    }

    case QuestionnaireSection.Feedback:
      return <OpenFeedback onSubmit={submitFeedback} />;
    case QuestionnaireSection.Complete:
      return <ThankYouPage />;
    default:
      return null;
  }
};
